import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router';
import { app, adminRoutes, portalRoutes, publicRoutes } from '@ircf/config';
import { PublicLayout } from '../layouts/PublicLayout.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { PortalLayout } from '../layouts/PortalLayout.js';
import { AdminLayout } from '../layouts/AdminLayout.js';
import { RouteError } from '../components/RouteError.js';
import { RouteFallback } from '../components/RouteFallback.js';
import { NotFoundPage } from '../pages/NotFoundPage.js';
import { RedirectIfAuthenticated, RequireApproval, RequireOnboarding, RequireRole } from './guards.js';

/**
 * Route table.
 *
 * Three properties are deliberate:
 *
 *  - Paths come from `@ircf/config`, never from string literals here, so the
 *    router, navigation, sitemap and E2E suite cannot drift apart.
 *  - Every page is lazily imported. The three public experiences each pull in
 *    their own 3D scene chunk, and the brief forbids loading all three at once;
 *    lazy boundaries are what make that true rather than aspirational.
 *  - The portal and admin trees are separate lazy subtrees, so a client never
 *    downloads admin code and no portal route pulls in Three.js.
 */

/** Wraps a lazy page in a suspense boundary with a route-appropriate skeleton. */
function page(
  loader: () => Promise<{ default: ComponentType }>,
  variant: 'public' | 'portal' = 'public',
): ReactNode {
  const Component = lazy(loader);
  return (
    <Suspense fallback={<RouteFallback variant={variant} />}>
      <Component />
    </Suspense>
  );
}

// --- Public ------------------------------------------------------------------

const publicChildren: RouteObject[] = [
  { index: true, element: page(() => import('../pages/public/HomePage.js')) },
  {
    path: publicRoutes.personalTraining.path.slice(1),
    element: page(() => import('../pages/public/PersonalTrainingPage.js')),
  },
  {
    path: publicRoutes.boxing.path.slice(1),
    element: page(() => import('../pages/public/BoxingPage.js')),
  },
  // Legacy public URL - keep bookmarks and shares working after the path moved.
  { path: 'boxing', element: <Navigate to={publicRoutes.boxing.path} replace /> },
  { path: 'boxing/', element: <Navigate to={publicRoutes.boxing.path} replace /> },
  {
    path: publicRoutes.programs.path.slice(1),
    element: page(() => import('../pages/public/ProgramsPage.js')),
  },
  {
    path: publicRoutes.results.path.slice(1),
    element: page(() => import('../pages/public/ResultsPage.js')),
  },
  {
    path: publicRoutes.about.path.slice(1),
    element: page(() => import('../pages/public/AboutPage.js')),
  },
  {
    path: publicRoutes.book.path.slice(1),
    element: page(() => import('../pages/public/BookPage.js')),
  },
  {
    path: publicRoutes.contact.path.slice(1),
    element: page(() => import('../pages/public/ContactPage.js')),
  },
  {
    path: publicRoutes.privacy.path.slice(1),
    element: page(() => import('../pages/legal/PrivacyPage.js')),
  },
  {
    path: publicRoutes.terms.path.slice(1),
    element: page(() => import('../pages/legal/TermsPage.js')),
  },
  {
    path: publicRoutes.trainingDisclaimer.path.slice(1),
    element: page(() => import('../pages/legal/TrainingDisclaimerPage.js')),
  },
  {
    path: publicRoutes.nutritionDisclaimer.path.slice(1),
    element: page(() => import('../pages/legal/NutritionDisclaimerPage.js')),
  },
  {
    path: publicRoutes.cancellationPolicy.path.slice(1),
    element: page(() => import('../pages/legal/CancellationPolicyPage.js')),
  },
  { path: publicRoutes.accessibility.path.slice(1),
    element: page(() => import('../pages/legal/AccessibilityPage.js')),
  },
  { path: '*', element: <NotFoundPage /> },
];

// --- Authentication ----------------------------------------------------------

const authChildren: RouteObject[] = [
  {
    path: publicRoutes.login.path.slice(1),
    element: (
      <RedirectIfAuthenticated>
        {page(() => import('../pages/auth/LoginPage.js'), 'portal')}
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: publicRoutes.register.path.slice(1),
    element: (
      <RedirectIfAuthenticated>
        {page(() => import('../pages/auth/RegisterPage.js'), 'portal')}
      </RedirectIfAuthenticated>
    ),
  },
  {
    path: publicRoutes.forgotPassword.path.slice(1),
    element: page(() => import('../pages/auth/ForgotPasswordPage.js'), 'portal'),
  },
  {
    path: publicRoutes.resetPassword.path.slice(1),
    element: page(() => import('../pages/auth/ResetPasswordPage.js'), 'portal'),
  },
  {
    path: publicRoutes.verifyEmail.path.slice(1),
    element: page(() => import('../pages/auth/VerifyEmailPage.js'), 'portal'),
  },
];

// --- Client portal -----------------------------------------------------------

/** Strips the `/portal/` prefix, since these are nested under the layout route. */
function portalPath(path: string): string {
  return path.replace(/^\/portal\/?/, '');
}

const portalChildren: RouteObject[] = [
  { index: true, element: <Navigate to={portalRoutes.dashboard.path} replace /> },
  {
    path: portalPath(portalRoutes.onboarding.path),
    element: page(() => import('../pages/portal/OnboardingPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.awaitingApproval.path),
    element: page(() => import('../pages/portal/AwaitingApprovalPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.dashboard.path),
    element: page(() => import('../pages/portal/DashboardPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.book.path),
    element: page(() => import('../pages/portal/BookPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.calendar.path),
    element: page(() => import('../pages/portal/CalendarPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.sessions.path),
    element: page(() => import('../pages/portal/SessionsPage.js'), 'portal'),
  },
  {
    path: `${portalPath(portalRoutes.sessions.path)}/:sessionId`,
    element: page(() => import('../pages/portal/SessionDetailPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.training.path),
    element: page(() => import('../pages/portal/TrainingPage.js'), 'portal'),
  },
  {
    path: `${portalPath(portalRoutes.training.path)}/:programId`,
    element: page(() => import('../pages/portal/TrainingProgramPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.workouts.path),
    element: page(() => import('../pages/portal/WorkoutsPage.js'), 'portal'),
  },
  {
    path: `${portalPath(portalRoutes.workouts.path)}/:workoutId`,
    element: page(() => import('../pages/portal/WorkoutLoggerPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.nutrition.path),
    element: page(() => import('../pages/portal/NutritionPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.checkIns.path),
    element: page(() => import('../pages/portal/CheckInsPage.js'), 'portal'),
  },
  {
    path: `${portalPath(portalRoutes.checkIns.path)}/:checkInId`,
    element: page(() => import('../pages/portal/CheckInFormPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.progress.path),
    element: page(() => import('../pages/portal/ProgressPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.analysis.path),
    element: page(() => import('../pages/portal/AnalysisPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.feedback.path),
    element: page(() => import('../pages/portal/FeedbackPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.messages.path),
    element: page(() => import('../pages/portal/MessagesPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.documents.path),
    element: page(() => import('../pages/portal/DocumentsPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.forms.path),
    element: page(() => import('../pages/portal/FormsPage.js'), 'portal'),
  },
  {
    path: `${portalPath(portalRoutes.forms.path)}/:assignmentId`,
    element: page(() => import('../pages/portal/FormFillPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.notifications.path),
    element: page(() => import('../pages/portal/NotificationsPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.profile.path),
    element: page(() => import('../pages/portal/ProfilePage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.settings.path),
    element: page(() => import('../pages/portal/SettingsPage.js'), 'portal'),
  },
  {
    path: portalPath(portalRoutes.support.path),
    element: page(() => import('../pages/portal/SupportPage.js'), 'portal'),
  },
];

// --- Admin portal ------------------------------------------------------------

function adminPath(path: string): string {
  return path.replace(/^\/admin\/?/, '');
}

const adminChildren: RouteObject[] = [
  { index: true, element: <Navigate to={adminRoutes.dashboard.path} replace /> },
  {
    path: adminPath(adminRoutes.dashboard.path),
    element: page(() => import('../pages/admin/DashboardPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.calendar.path),
    element: page(() => import('../pages/admin/CalendarPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.bookings.path),
    element: page(() => import('../pages/admin/BookingsPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.clients.path),
    element: page(() => import('../pages/admin/ClientsPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.client.path),
    element: page(() => import('../pages/admin/ClientWorkspacePage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.trainingPlans.path),
    element: page(() => import('../pages/admin/TrainingPlansPage.js'), 'portal'),
  },
  {
    path: `${adminPath(adminRoutes.trainingPlans.path)}/:programId`,
    element: page(() => import('../pages/admin/TrainingProgramBuilderPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.nutritionPlans.path),
    element: page(() => import('../pages/admin/NutritionPlansPage.js'), 'portal'),
  },
  {
    path: `${adminPath(adminRoutes.nutritionPlans.path)}/:planId`,
    element: page(() => import('../pages/admin/NutritionPlanBuilderPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.exercises.path),
    element: page(() => import('../pages/admin/ExercisesPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.checkIns.path),
    element: page(() => import('../pages/admin/CheckInsPage.js'), 'portal'),
  },
  {
    path: `${adminPath(adminRoutes.checkIns.path)}/:checkInId`,
    element: page(() => import('../pages/admin/CheckInReviewPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.forms.path),
    element: page(() => import('../pages/admin/FormsPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.feedback.path),
    element: page(() => import('../pages/admin/FeedbackPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.availability.path),
    element: page(() => import('../pages/admin/AvailabilityPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.messages.path),
    element: page(() => import('../pages/admin/MessagesPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.documents.path),
    element: page(() => import('../pages/admin/DocumentsPage.js'), 'portal'),
  },
  {
    path: adminPath(adminRoutes.notifications.path),
    element: page(() => import('../pages/admin/NotificationsPage.js'), 'portal'),
  },
  // Administrator-only from here down. The guard is nested rather than repeated
  // per route so the role list has exactly one definition.
  {
    element: (
      <RequireRole roles={adminRoutes.settings.roles}>
        <Outlet />
      </RequireRole>
    ),
    children: [
      {
        path: adminPath(adminRoutes.sessionTypes.path),
        element: page(() => import('../pages/admin/SessionTypesPage.js'), 'portal'),
      },
      {
        path: adminPath(adminRoutes.packages.path),
        element: page(() => import('../pages/admin/PackagesPage.js'), 'portal'),
      },
      {
        path: adminPath(adminRoutes.content.path),
        element: page(() => import('../pages/admin/ContentPage.js'), 'portal'),
      },
      {
        path: adminPath(adminRoutes.media.path),
        element: page(() => import('../pages/admin/MediaPage.js'), 'portal'),
      },
      {
        path: adminPath(adminRoutes.results.path),
        element: page(() => import('../pages/admin/ResultsPage.js'), 'portal'),
      },
      {
        path: adminPath(adminRoutes.reports.path),
        element: page(() => import('../pages/admin/ReportsPage.js'), 'portal'),
      },
      {
        path: adminPath(adminRoutes.settings.path),
        element: page(() => import('../pages/admin/SettingsPage.js'), 'portal'),
      },
      {
        path: adminPath(adminRoutes.audit.path),
        element: page(() => import('../pages/admin/AuditPage.js'), 'portal'),
      },
    ],
  },
];

// --- Assembly ----------------------------------------------------------------

export const routeObjects: RouteObject[] = [
  {
    errorElement: <RouteError />,
    children: [
      {
        path: publicRoutes.coachCard.path,
        element: page(() => import('../pages/public/CoachCardPage.js')),
      },
      { path: '/', element: <PublicLayout />, children: publicChildren },
      { element: <AuthLayout />, children: authChildren },
      {
        path: portalRoutes.root.path,
        element: (
          <RequireRole roles={portalRoutes.dashboard.roles}>
            <RequireApproval>
              <RequireOnboarding>
                <PortalLayout />
              </RequireOnboarding>
            </RequireApproval>
          </RequireRole>
        ),
        children: portalChildren,
      },
      {
        path: adminRoutes.root.path,
        element: (
          <RequireRole roles={adminRoutes.dashboard.roles}>
            <AdminLayout />
          </RequireRole>
        ),
        children: adminChildren,
      },
    ],
  },
];

/**
 * The router is created with the deployment base path so every `<Link>` and
 * every `navigate()` call resolves under `/clients/<slug>` without a single
 * component knowing the prefix exists.
 */
export function createRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(routeObjects, {
    basename: app.basePath === '' ? undefined : app.basePath,
  });
}
